import React from 'react';
import LegalLayout from './LegalLayout';

const EFFECTIVE_DATE = "1 February 2026";
const SUPPORT_EMAIL = "miiglobalpartner@gmail.com";

export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Service"
      metaTitle="Terms of Service | TelegramEmailBot"
      metaDescription="The terms that govern your use of TelegramEmailBot. Plain English, fair rules. Read before signing up — by using the service you agree to them."
      canonicalPath="/terms"
      testid="terms-page"
    >
      <p className="text-sm text-gray-500"><em>Effective date: {EFFECTIVE_DATE}</em></p>

      <p>
        Welcome to <strong>TelegramEmailBot</strong> ("the Service"), operated by Mii Global Partner Holdings Sdn. Bhd.. By starting the bot, creating an account, or paying for a subscription, you agree to these Terms of Service. If you do not agree, please stop using the Service.
      </p>

      <h2>1. The service</h2>
      <p>
        TelegramEmailBot assigns you a unique email address that delivers incoming emails to your chosen Telegram chat or group. You can also send emails from Telegram, attach files, and use optional AI summaries. Features may change as we improve the product.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 13 years old (or the age of digital consent in your country) to use the Service. By using it, you confirm that you have the legal capacity to enter into a binding contract.
      </p>

      <h2>3. Pricing and paid subscription</h2>
      <ul>
        <li>To get your email address you purchase the <strong>$4.99 one-time Starter Email</strong>, which includes your own inbox and 20 complimentary incoming emails. Your email address is created and revealed only after payment.</li>
        <li><strong>Unpaid full inboxes are deleted after 14 days.</strong> When a Starter mailbox has used all 20 complimentary emails, new emails are held and a 14-day grace period begins. If the mailbox is not upgraded to Inbox Storage &amp; AI ($14.99/year) within those 14 days, the address is permanently released and any held emails are deleted. We send reminders on day 0, day 7 and day 13 before this happens.</li>
        <li>Continued unlimited use requires the paid Inbox Storage &amp; AI subscription: <strong>USD 14.99 per year</strong>.</li>
        <li>Subscriptions are billed by Stripe. Renewals are automatic unless you cancel before the renewal date.</li>
        <li>You can cancel anytime from within Telegram or by emailing us. Cancellation stops future renewals; we do not pro-rate refunds for the current period.</li>
      </ul>

      <h2>4. Refunds &amp; 7-day money-back guarantee</h2>
      <p>
        The <strong>$4.99 Starter Email</strong> comes with a <strong>7-day money-back guarantee</strong>: if you are not satisfied within 7 days of purchase, email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> with your Telegram chat ID and transaction reference and we will refund your $4.99 in full, no questions asked.
      </p>
      <p>
        For the <strong>Inbox Storage &amp; AI</strong> subscription, we also offer refunds within 7 days of the subscription start, provided the Service has not been substantially used. After 7 days, subscription payments are non-refundable, though you keep access until the end of your paid period. The $2.99 Email Address Change fee is non-refundable once the new address has been issued.
      </p>

      <h2>5. Acceptable use</h2>
      <p>You agree NOT to use TelegramEmailBot to:</p>
      <ul>
        <li>Send unsolicited bulk email (spam), phishing, malware or any unlawful content</li>
        <li>Impersonate any person or entity</li>
        <li>Harass, threaten or harm others</li>
        <li>Distribute child sexual abuse material or content that incites violence</li>
        <li>Bypass rate limits, abuse the API, or attempt to reverse engineer the Service</li>
        <li>Resell or sublicense the Service without a written agreement</li>
      </ul>
      <p>
        We reserve the right to suspend or terminate accounts that violate these rules, without refund. Severe violations may be reported to law enforcement.
      </p>

      <h2>6. Your content</h2>
      <p>
        You retain full ownership of the emails you send and receive through the Service. We only process this content to deliver, store and display it as required by the Service. We never sell, mine or train AI models on your content.
      </p>

      <h2>7. Service availability</h2>
      <p>
        We aim for high uptime but the Service is provided "as is" without uptime guarantees. Telegram, Gmail, Brevo, Stripe and other third-party providers may experience outages that affect us. We are not liable for indirect losses caused by such outages.
      </p>

      <h2>8. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, our total liability to you for any claim related to the Service is limited to the amount you have paid us in the 12 months preceding the claim. We are not liable for indirect, incidental, special, consequential or punitive damages.
      </p>

      <h2>9. Intellectual property</h2>
      <p>
        The name "TelegramEmailBot", our logo, branding, code and design are owned by Mii Global Partner Holdings Sdn. Bhd.. You may not copy, modify, redistribute or create derivative works without written permission.
      </p>

      <h2>10. Termination</h2>
      <p>
        You may stop using the Service at any time. We may suspend or terminate accounts that violate these Terms, are inactive for an extended period, or for legal reasons. On termination your data will be deleted within 30 days (subject to legal retention requirements).
      </p>

      <h2>11. Changes to these terms</h2>
      <p>
        We may update these Terms occasionally. Material changes will be announced via our Telegram support channel and shown at the top of this page. Continued use after a change means you accept the updated Terms.
      </p>

      <h2>12. Governing law</h2>
      <p>
        These Terms are governed by the laws of Malaysia. Any dispute will first be attempted to be resolved amicably; failing that, it will be submitted to the courts of Penang, Malaysia.
      </p>

      <h2>13. Contact</h2>
      <p>
        Mii Global Partner Holdings Sdn. Bhd.<br />
        17, Taman Bukit Dumbar, Jelutong, Penang 11600, Malaysia<br />
        Email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
      </p>
    </LegalLayout>
  );
}
